import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    computed,
    effect,
    inject,
    input,
} from '@angular/core';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import type { PaymentElement, PaymentMethod } from '@integrations/payment-methods';
import {
    PaymentMethodStatus,
    mapAvailablePaymentMethods,
    mapPaymentMethodCurrencies,
} from '@integrations/payment-methods';
import type { PaymentElementLegacy } from '@sites/entity-core';
import { createPaymentElementLegacy } from '@sites/entity-core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';
import { EPaymentType } from '@common/enums';

import { SpControlHandleComponent } from '@libs/ui/sp-control-handle/src/sp-control-handle.component';
import { findActivePaymentMethod, getDefaultPaymentElementLegacyData } from './web-payment-methods-collection.helpers';

/**
 * Signal-form sibling of `web-payment-methods-collection`. The FormArray
 * version stays in place for the still-FormGroup shop-cart widget; this one is
 * pure value-in / event-out over a plain `PaymentElementLegacy[]`, used by the
 * signal-form payment-form widget. The default-element seeding effect mirrors
 * the FormArray version's constructor effect but emits the next array instead
 * of pushing into a FormArray.
 */
@Component({
    selector: 'web-payment-methods-signal-collection',
    templateUrl: './web-payment-methods-signal-collection.component.html',
    styleUrls: ['./web-payment-methods-collection.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkDropList, CdkDrag, CdkDropListGroup, SpControlHandleComponent, TranslateModule],
})
export class WebPaymentMethodsSignalCollectionComponent {
    private readonly translateService = inject(TranslateService);

    public readonly paymentElements = input.required<PaymentElementLegacy[]>();
    public readonly isShopCart = input<boolean>(false);

    public readonly paymentMethods = input.required<PaymentMethod[]>();
    public readonly isLoading = input.required<boolean>();
    public readonly isFailure = input.required<boolean>();

    @Output() public editActionEvent: EventEmitter<number> = new EventEmitter();
    @Output() public setPaymentElementsEvent: EventEmitter<PaymentElementLegacy[]> = new EventEmitter();
    @Output() public setSkipHistoryEvent: EventEmitter<void> = new EventEmitter();

    public readonly availablePaymentMethods = computed(() => mapAvailablePaymentMethods(this.paymentMethods()));
    public readonly paymentMethodsCurrencies = computed(() => mapPaymentMethodCurrencies(this.paymentMethods()));

    constructor() {
        let initialized = false;
        effect(() => {
            const paymentMethods = this.paymentMethods();
            const paymentElements = this.paymentElements();

            if (initialized) return;
            if (!paymentMethods?.length) return;
            if (paymentElements.at(0)?.payment_method_id) {
                initialized = true;
                return;
            }

            const defaultData = getDefaultPaymentElementLegacyData(findActivePaymentMethod(paymentMethods));

            this.setSkipHistoryEvent.emit();

            if (!paymentElements.length) {
                this.setPaymentElementsEvent.emit([this.createPaymentElement(defaultData)]);
                initialized = true;
                return;
            }

            this.setPaymentElementsEvent.emit([{ ...paymentElements[0], ...defaultData }, ...paymentElements.slice(1)]);
            initialized = true;
        });
    }

    /**
     * Every element must be born with the full key set — the entity factory's
     * twin of the FormArray version's `paymentElementFormGroup(translate)`.
     * Keys missing from the object never materialise as signal-form `FieldTree`
     * children, so `[formField]` bindings in the settings panel have nothing to
     * bind to and `customPaymentName`-based validity can never be cleared.
     */
    private createPaymentElement(data: Partial<PaymentElementLegacy>): PaymentElementLegacy {
        return createPaymentElementLegacy({
            customPaymentName: this.translateService.instant('custom_payment_label'),
            ...data,
        });
    }

    public dropPaymentElementItem(event: CdkDragDrop<PaymentElementLegacy[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const buttons = [...this.paymentElements()];
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);

        this.setPaymentElementsEvent.emit(buttons);
    }

    public addPaymentElement(): void {
        const paymentMethods = this.paymentMethods();
        const paymentElements = this.paymentElements();
        const usedPaymentMethods = paymentElements.map((paymentElement) => paymentElement.type);
        let paymentMethod: PaymentMethod = paymentMethods.find(
            (method: PaymentMethod) =>
                !usedPaymentMethods.includes(method.type) && method.status === PaymentMethodStatus.active
        );
        if (!paymentMethod) {
            paymentMethod = {
                type: EPaymentType.custom,
                id: 'custom_payment_id',
                name: paymentTypeNames[EPaymentType.custom],
                status: PaymentMethodStatus.active,
                settings: null,
                isDefault: false,
            };
        }

        const next = this.createPaymentElement({
            type: paymentMethod.type,
            payment_method_id: paymentMethod.id,
            isEditableTotalCost: false,
            currency: this.paymentMethodsCurrencies()[paymentMethod.type] || 'USD',
            isActive: true,
        });

        this.setPaymentElementsEvent.emit([...paymentElements, next]);
    }

    public removePaymentElement(index: number): void {
        this.setPaymentElementsEvent.emit(this.paymentElements().filter((_, i) => i !== index));
    }

    public trackByPaymentElementIndex(index: number): number {
        return index;
    }

    public getPaymentElementSubText(paymentElement: PaymentElementLegacy): string {
        const available = this.availablePaymentMethods();
        if (!available || !available[paymentElement.type]) {
            return this.translateService.instant('payment_method_is_not_active');
        }

        if (!paymentElement.amount) {
            return this.translateService.instant('payment_amount_invalid');
        }

        return `${this.translateService.instant('control_price')} ${paymentElement.amount} ${paymentElement.currency}`;
    }

    public getPaymentElementLabel(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType && paymentElementType !== EPaymentType.custom) {
            return null;
        }

        return paymentsList[paymentElementType].label;
    }

    public getPaymentElementIcon(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType && paymentElementType !== EPaymentType.custom) {
            return null;
        }

        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[paymentElementType]}-sm.svg`;
    }
}
